import { useSettings } from "@app-composables/settings";
import usei18n from "@app-i18n/index";
import useCartRestClient from "@app-rest/cart";
import { IResponseError } from "@app-rest/index";
import useReservationRestClient from "@app-rest/reservations";
import type { IBowlingMandatoryPage, IModifierGroupBE, IOfferSelected, IReservationBowlerOptions, IReservationBowlerOptionsWithSize, IReservationBowlerRequestOptions, IReservationConfirmData, IReservationConfirmResponse, IReservationData, IWebOfferOptions, PaymentStatus, ReservationConfirmError, ReservationStatus } from "@app-rest/reservations.interfaces";
import { type IOpeningDate, type IPlayersQuantity } from "@app-rest/systems.interfaces";
import { extrasPage, foodBeveragePage, footwearsPage } from "@app-router/routes/reservations";
import useSystemStore from "@app-store/system";
import useUserStore, { UserModuleInit } from "@app-store/user";
import type { BowlingMandatoryPage } from "@app-utilities/const";
import { dateKey, stringToDateUTC } from "@app-utilities/dates";
import { deepToRaw, limitToChars } from "@app-utilities/func";
import { trackUserEvent } from "@app-utilities/observability";
import { AppInsightsEvent } from "@app-utilities/observability-events";
import { getPaymentPopup } from "@app-utilities/popup";
import { clearFluxPersistence, persistenceKey } from "@app-utilities/reservation-flux";
import type { LoadingCallback } from "@qamf/payments";
import { getPaymentsAppUrl, PaymentError, PaymentProvider, providerRendersAs, setup as setupPaymentsEnv, usePaymentsInstance } from "@qamf/payments";
import { addMinutes } from "date-fns";
import type { IResponse } from "scarlett";
import { Action, Module, Mutation, VuexModule } from "vuex-class-modules";

import store from ".";
import useCartStore, { IModifierGroup } from "./cart";
import { getMutationContext, IBaseContext, IPersistentModule, resolveMutationContext, usePersistentModule } from "./persistent.plugin";
import { FluxView } from "./reservation-flux.interfaces";

export type IPayAndConfirmResultPayload = {
	providerReplied: boolean,
	providerData: {[k: string]: string},
}

export interface IReservationConfirmWithoutPaymentResult extends IResponse<IReservationConfirmResponse, IResponseError<ReservationConfirmError>> {
	paymentHappened: false,
}
export interface IReservationConfirmWithPaymentResult extends IPayAndConfirmResultPayload {
	paymentHappened: true,
}

export type IReservationConfirmResult = IReservationConfirmWithoutPaymentResult | IReservationConfirmWithPaymentResult

type TransactionIdentifiers = {
	OperationId: string,
	OrderId: string,
}

export interface IPaypalCompletionPayload {
	OperationId: string;
	OrderId: string;
	Checkout: "Cancel" | "Complete";
}

@Module class ReservationStoreFactory extends VuexModule implements IPersistentModule {
	loadingReservationApi = false;
	loadingConfirmPaymentApi = false;
	openingDayCached: IOpeningDate | null = null;
	formPickedDatetime: Date | null = null;
	pickedDatetime: Date | null = null;
	bowlers: IPlayersQuantity[] = [];
	bowlersTotal = 0;
	offer: IOfferSelected | null = null;
	options: IWebOfferOptions | null = null;
	foodBeverageModifierGroups = new Map<number, IModifierGroup[]>();
	shoesTotal = 0;
	operationId: string | null = null;
	reservationKey: string | null = null;
	reservationToken: string | null = null;
	reservationKeyExpire: Date | null = null;
	reservationStatus: ReservationStatus | null = null;
	reservationNeedPayment: boolean | null = null;
	paymentStatus: PaymentStatus | null = null;
	latestView: FluxView | null = null;
	players: IReservationBowlerOptions[] = [];
	persistenceInitialized = false;
	bowlingMandatoryPages: IBowlingMandatoryPage[] | null = null;
	openingDatetimeOpen() {
		if (!this.openingDayCached) return;
		const d = this.openingDayCached;
		return d.StartBookingTime ? stringToDateUTC(d.StartBookingTime) : undefined;
	}

	openingDatetimeClose() {
		if (!this.openingDayCached) return;
		const d = this.openingDayCached;
		return d.EndBookingTime ? stringToDateUTC(d.EndBookingTime) : undefined;
	}

	getData(): IReservationData {
		return {
			systemId: Number(useSystemStore().info?.Id),
			offer: this.offer,
			formPickedDatetime: this.formPickedDatetime,
			pickedDatetime: this.pickedDatetime,
			bowlers: this.bowlers,
			bowlersTotal: this.bowlersTotal,
			shoesTotal: this.shoesTotal,
			reservationKey: this.reservationKey,
			reservationToken: this.reservationToken,
			reservationKeyExpire: this.reservationKeyExpire,
			reservationStatus: this.reservationStatus,
			reservationNeedPayment: this.reservationNeedPayment,
			paymentStatus: this.paymentStatus,
			latestView: this.latestView,
			mandatoryPages: this.bowlingMandatoryPages
		};
	}

	generatePlayersFromBowlers() {
		if (this.bowlers && this.bowlers.length) {
			const bowlerModels: IReservationBowlerOptionsWithSize[] = [];
			this.bowlers.forEach((playerCategory) => {
				for (let i = 0; i < playerCategory.Quantity; i++) {
					bowlerModels.push({
						Name: null,
						ShoeSize: null,
						WantBumpers: false,
						Size: null
					});
				}
			});
			const firstName = useUserStore().FirstName;
			bowlerModels[0].Name = firstName
				? limitToChars(firstName, 21)
				: "";

			return bowlerModels;
		}

		return [];
	}

	hasReservationFormData() {
		return Boolean(Number(useSystemStore().info?.Id) && this.pickedDatetime && this.bowlersTotal);
	}

	isReservationConfirmed() {
		return this.reservationStatus === "CONFIRMED";
	}

	isReservationTemporary() {
		return this.reservationStatus === "TEMPORARY";
	}

	@Mutation setData(data: IReservationData) {
		Object.keys(data).forEach(key => {
			this[key] = data[key];
		});
	}

	@Mutation clearData() {
		this.openingDayCached = null;
		this.formPickedDatetime = null;
		this.pickedDatetime = null;
		this.bowlers = [];
		this.bowlersTotal = 0;
		this.offer = null;
		this.options = null;
		this.foodBeverageModifierGroups.clear();
		this.shoesTotal = 0;
		this.operationId = null;
		this.reservationKey = null;
		this.reservationToken = null;
		this.reservationKeyExpire = null;
		this.reservationStatus = null;
		this.reservationNeedPayment = null;
		this.paymentStatus = null;
		this.latestView = null;
		this.players = [];
		this.bowlingMandatoryPages = null;
	}

	@Mutation clearReservation() {
		this.offer = null;
		this.options = null;
		this.operationId = null;
		this.reservationKey = null;
		this.reservationToken = null;
		this.reservationKeyExpire = null;
		this.reservationStatus = null;
		this.reservationNeedPayment = null;
		this.paymentStatus = null;
		this.bowlingMandatoryPages = null;
	}

	@Mutation setPersistenceReady() {
		this.persistenceInitialized = true;
	}

	@Mutation setLatestView(view: FluxView | null) {
		this.latestView = view;
	}

	@Mutation setReservationToken(token: string | null) {
		if (this.reservationToken === token) return;

		this.reservationToken = token;
	}

	@Mutation cacheOpeningDay(date: Date | null) {
		if (!date) {
			this.openingDayCached = null;
			return;
		}
		const dateString = dateKey(date);
		const cached = useSystemStore().getOpeningDay(dateString);
		this.openingDayCached = cached ?? null;
	}

	@Mutation setBowlers(bowlers: IPlayersQuantity[]) {
		this.bowlers = bowlers;
		this.bowlersTotal = bowlers.reduce((acc, val) => { return acc + val.Quantity }, 0);
	}

	@Mutation setFormPickedDatetime(datetime: Date | null) {
		this.formPickedDatetime = datetime;
	}

	@Mutation setPickedDatetime(datetime: Date) {
		this.pickedDatetime = datetime;
	}

	@Mutation setLoadingReservationApi(state: boolean) {
		this.loadingReservationApi = state;
	}

	@Mutation setLoadingConfirmPaymentApi(state: boolean) {
		this.loadingConfirmPaymentApi = state;
	}

	@Mutation setSelectedOffer(offer: IOfferSelected) {
		this.offer = offer;
		this.pickedDatetime = offer.Datetime;
		this.reservationStatus = null;
		this.options = null;
	}

	@Mutation setOptions(options: IWebOfferOptions | null) {
		this.options = options;
	}

	@Mutation setShoesTotal(total: number) {
		this.shoesTotal = total;
	}

	@Mutation setPlayers(players: IReservationBowlerOptions[] | []) {
		this.players = players ?? [];
	}

	@Mutation setOperationId(id: string | null) {
		this.operationId = id;
	}

	@Mutation setReservationKey(key: string | null) {
		this.reservationKey = key;
		if (key)
			useUserStore().reservationsHistoryAdd(key);
	}

	@Mutation updateReservationKeyExpire(minutes = 10) {
		const expire = addMinutes(new Date(), minutes);
		this.reservationKeyExpire = expire;
	}

	@Mutation setReservationStatus(status: ReservationStatus | null) {
		this.reservationStatus = status;
		if (status === "CONFIRMED" && this.reservationKey) {
			useUserStore().reservationsHistoryRemove(this.reservationKey);
			clearFluxPersistence();
		}
	}

	@Mutation setReservationNeedPayment(status: boolean | null) {
		this.reservationNeedPayment = status;
	}

	@Mutation setPaymentStatus(status: PaymentStatus | null) {
		this.paymentStatus = status;
	}

	@Mutation setBowlingMandatoryPages(pages: IBowlingMandatoryPage[] | null) {
		this.bowlingMandatoryPages = pages;
	}

	@Mutation setBowlingMandatoryPageVisited(pageName: BowlingMandatoryPage) {
		if (!this.bowlingMandatoryPages) return;

		const pageToSetVisitedIndex = this.bowlingMandatoryPages.findIndex(el => el.name === pageName);

		if (pageToSetVisitedIndex === -1) return;

		this.bowlingMandatoryPages[pageToSetVisitedIndex].visited = true;
	}

	private mapModifiers(modifiersGroups: IModifierGroupBE[], originalId?: number, quantity?: number) {
		const foodModifiers = modifiersGroups.filter(group => group.Modifiers && group.Modifiers.length > 0)
			.map(group => {
				let computedId = group.IdModifierGroup.toString();
				if (originalId) computedId += `-${originalId}`;
				const mappedGroup: IModifierGroup = {
					Title: group.Name,
					Id: group.IdModifierGroup.toString(),
					Rules: group.Rules,
					Modifiers: [],
					ComputedId: computedId
				};
				mappedGroup.Modifiers = group.Modifiers.map(mod => ({
					Id: mod.IdOriginal.toString(),
					Name: mod.Name,
					Price: mod.Price,
					GroupId: group.IdModifierGroup.toString(),
					...(originalId && { ReferencePriceKeyId: originalId }),
					...(typeof quantity === "number" && { ReferencePriceKeyQuantity: quantity })
				}));
				return mappedGroup;
			});
		return foodModifiers;
	}

	@Action async ensureItemFoodBeverageModifiers(pricekeyId: number) {
		if (this.foodBeverageModifierGroups.get(pricekeyId)) return;

		const restClient = useReservationRestClient();
		const response = await restClient.getItemFoodBeverageModifiers(Number(useSystemStore().info?.Id), pricekeyId);
		if (!response.data || response.data.ModifiersGroups.length === 0) return this.foodBeverageModifierGroups.clear();
		const modifiers = this.mapModifiers(response.data.ModifiersGroups);
		this.foodBeverageModifierGroups.set(pricekeyId, modifiers);
	}

	@Action async ensurePackageFoodBeverageModifiers(pricekeyId: number) {
		if (this.foodBeverageModifierGroups.get(pricekeyId)) return;

		const restClient = useReservationRestClient();
		const response = await restClient.getPackageFoodBeverageModifiers(Number(useSystemStore().info?.Id), pricekeyId);
		if (!response.data
			|| !response.data.PackageItems
			|| response.data.PackageItems.length === 0) return this.foodBeverageModifierGroups.clear();

		const foodModifiers = <IModifierGroup[]>[];
		response.data.PackageItems.forEach(pck => {
			if (pck.ModifiersGroups && pck.ModifiersGroups.length > 0) {
				const packMod = this.mapModifiers(pck.ModifiersGroups, pck.IdOriginal, pck.Quantity);
				foodModifiers.push(...packMod);
			}
		});
		this.foodBeverageModifierGroups.set(pricekeyId, foodModifiers);
	}

	@Action async reservationCreate() {
		if (!this.offer || !this.pickedDatetime) return;

		this.setLoadingReservationApi(true);
		const restClient = useReservationRestClient();
		const response = await restClient.reservationCreate(Number(useSystemStore().info?.Id), {
			DateFrom: this.pickedDatetime,
			WebOfferId: this.offer.OfferId,
			WebOfferTariffId: this.offer.ItemId,
			PlayersList: this.bowlers.map(b => ({
				TypeId: b.Id,
				Number: b.Quantity
			}))
		});
		if (response.error) {
			this.setReservationKey(null);
			this.updateReservationKeyExpire(0);
		} else if (response.data) {
			const token = response.fetchResponse?.headers?.get("x-sessiontoken") ?? null;
			if (!token)
				trackUserEvent(AppInsightsEvent.ReservationTokenMissingOnCreate);


			this.setReservationToken(token);
			this.setReservationKey(response.data.ReservationKey);
			this.updateReservationKeyExpire(response.data.LifetimeMinutes);
		}

		this.setLoadingReservationApi(false);
		return response;
	}

	@Action async reservationTryRenew() {
		if (!this.reservationKey || this.loadingConfirmPaymentApi) return Promise.resolve(null);

		const CartStore = useCartStore();
		const reskey = String(this.reservationKey);
		const isBeforeNow = this.pickedDatetime && this.pickedDatetime <= new Date();
		const handleExit = async() => {
			useUserStore().reservationsHistoryRemove(reskey);
			this.setReservationKey(null);
			this.updateReservationKeyExpire(0);
			this.setLoadingReservationApi(false);
			await clearFluxPersistence();
		};
		if (isBeforeNow)
			return handleExit();
		this.setLoadingReservationApi(true);
		const restClient = useReservationRestClient();
		let response = await restClient.reservationRenew(Number(useSystemStore().info?.Id), reskey);
		let created = false;
		if (response.status >= 400) {
			let code = +response.status;
			if (code === 409 || code === 401) {
				handleExit();
				return response;
			}

			if (code === 404 && this.offer && this.pickedDatetime) {
				response = await restClient.reservationCreate(Number(useSystemStore().info?.Id), {
					DateFrom: this.pickedDatetime,
					WebOfferId: this.offer.OfferId,
					WebOfferTariffId: this.offer.ItemId,
					PlayersList: this.bowlers.map(b => ({
						TypeId: b.Id,
						Number: b.Quantity
					}))
				});
				code = +response.status;
				created = code < 400;
			}
			if (code >= 400) {
				handleExit();
				return response;
			}
		}
		if (created) {
			useUserStore().reservationsHistoryRemove(reskey);
			await clearFluxPersistence();
			this.setReservationStatus(null);
			this.setReservationNeedPayment(null);
			this.setPaymentStatus(null);
			CartStore.setIsPaid(false);
			const token = response.fetchResponse?.headers?.get("x-sessiontoken") ?? null;
			if (!token)
				trackUserEvent(AppInsightsEvent.ReservationTokenMissingOnRenew);


			this.setReservationToken(token);
		}

		if (!response.data) return Promise.resolve(null);
		this.setReservationKey(response.data.ReservationKey);
		this.updateReservationKeyExpire(response.data.LifetimeMinutes);
		this.setLoadingReservationApi(false);
		await CartStore.handleCreatedAt(CartStore.createdAt); // << write current states on store

		return response;
	}

	@Action async getReservationStatus() {
		if (!this.reservationKey) return Promise.resolve();
		const restClient = useReservationRestClient();
		return restClient.reservationStatus(Number(useSystemStore().info?.Id), this.reservationKey);
	}

	@Action async reservationSetEndFlow() {
		if (!this.reservationKey) return Promise.resolve();
		const restClient = useReservationRestClient();
		return restClient.reservationSetEndFlow(Number(useSystemStore().info?.Id), this.reservationKey);
	}

	@Action async callRollBackPayment() {
		if (!this.reservationKey) return Promise.resolve();

		const restClient = useReservationRestClient();
		const response = restClient.rollback(Number(useSystemStore().info?.Id), this.reservationKey);
		if ((await response).fetchResponse?.ok)
			this.setOperationId(null);

		return response;
	}

	@Action async reservationDelete() {
		if (this.isReservationConfirmed() || this.isReservationTemporary() || !this.reservationKey) return;
		const restClient = useReservationRestClient();
		await restClient.reservationDelete(Number(useSystemStore().info?.Id), this.reservationKey);
		useUserStore().reservationsHistoryRemove(this.reservationKey);
	}

	private paymentInstance: { dispose: () => void } | null = null;

	@Mutation setPaymentInstance(instance: { dispose: () => void } | null) {
		this.paymentInstance = instance;
	}

	@Action disposePaymentInstance() {
		if (this.paymentInstance) {
			this.paymentInstance.dispose();
			this.setPaymentInstance(null);
		}
	}

	// Tech Story 64265: [Bowler] Refactor reservationConfirm and reservationPay methods
	@Action async reservationPayAndFinalConfirm(payload: {
		response: IResponse<IReservationConfirmResponse, IResponseError<ReservationConfirmError>>,
		onLoadingChange?: LoadingCallback
	}): Promise<IReservationConfirmResult | PaymentError> {
		const { response, onLoadingChange } = payload;
		const systemStore = useSystemStore();

		if (response && response.error?.data?.Error?.Code === "ReservationAlreadyConfirmed") {
			this.setReservationStatus("CONFIRMED");
			this.setReservationNeedPayment(false);
			response.status = 200;
			return {
				paymentHappened: false,
				...response
			} satisfies IReservationConfirmWithoutPaymentResult;
		}

		if ((response && response.status >= 400) || (response && !response.data)) {
			this.setOperationId(null);
			this.setReservationStatus(null);
			this.setPaymentStatus(null);
			return {
				paymentHappened: false,
				...response
			} satisfies IReservationConfirmWithoutPaymentResult;
		}

		if (response.data?.NeedPayment && response.data?.ApprovePayment) {
			this.setOperationId(response.data?.OperationId ?? null);
			this.setReservationStatus(null);
			this.setPaymentStatus(null);
			const { translateKey } = usei18n();
			const currentProvider = systemStore.providerConfiguration?.ProviderName ?? "None";
			const paymentProvider = PaymentProvider[currentProvider];
			const paymentData = response.data.ApprovePayment;
			const url = paymentData.MobileUrl ?? paymentData.Url;
			const target = providerRendersAs(paymentProvider) === "popup" ? "hpp-payments-button" : "hpp-payments-target";
			const useSquareSdk = paymentProvider === PaymentProvider.Square && systemStore.functionalities?.SquareSdkIntegration?.Enabled;

			const CartStore = useCartStore();
			const formUiData = {
				OrderLabel: translateKey("label_deposit_only"),
				Deposit: CartStore.summaryData?.Deposit ?? 0,
				Subtotal: CartStore.summaryData?.TotalWithoutTaxes ?? 0,
				OrderTotal: CartStore.summaryData?.Total ?? 0,
				ItemCount: CartStore.getItemsQty()
			};

			const confirmSquarePayment = async(token: string, operationId: string) => {
				if (!systemStore.info?.Id)
					return { Success: false, OperationId: operationId, ErrorMessage: "Missing system id when sending square token", ErrorCodes: [] };

				const cartRestClient = useCartRestClient();
				const centerId = Number(systemStore.info.Id);

				const confirmResponse = await cartRestClient.squareSdkConfirm(centerId, {
					Token: token,
					OperationId: operationId
				});

				const responseData = confirmResponse.data;
				if (!responseData) throw new Error("Unhandled error when confirming square payment");

				return responseData;
			};

			const paymentInstance = useSquareSdk
				? await usePaymentsInstance(
					paymentProvider,
					{ Data: paymentData.Data, Method: paymentData.Method, Url: url, OperationId: this.operationId ?? "" },
					target,
					{ mode: "square-sdk", confirmPayment: confirmSquarePayment, translateKey, formUiData, onLoadingChange }
				)
				: await usePaymentsInstance(paymentProvider, { Data: paymentData.Data, Method: paymentData.Method, Url: url, OperationId: this.operationId ?? "" }, target);
			if (paymentInstance instanceof PaymentError) {
				this.setPaymentInstance(null);
				return paymentInstance;
			}

			// Save instance allowing explicit cleanup
			this.setPaymentInstance(paymentInstance);

			const result = await paymentInstance.resultAsync;
			if (!result) {
				this.setPaymentInstance(null);
				return new PaymentError("NoResult", "No result received from the payment provider.");
			}

			if (result instanceof PaymentError) {
				this.setPaymentInstance(null);
				return result;
			}

			if (!result.data) {
				this.setPaymentInstance(null);
				return new PaymentError("NoResultData", "No result data received from the payment provider.");
			}

			paymentInstance.dispose();
			this.setPaymentInstance(null);

			return {
				paymentHappened: true,
				...result.data
			} satisfies IReservationConfirmWithPaymentResult;
		}

		this.setOperationId(null);
		this.setPaymentStatus(null);
		this.setReservationStatus("CONFIRMED");
		const userStore = useUserStore();
		if (!userStore.isLoggedIn())
			userStore.clearPersistedData();

		return {
			paymentHappened: false,
			...response
		} satisfies IReservationConfirmWithoutPaymentResult;
	}

	@Action async reservationConfirm(): Promise<IResponse<IReservationConfirmResponse, IResponseError<ReservationConfirmError>>> {
		if (!this.reservationKey)
			throw new Error("Reservation key is missing on reservationConfirm");

		const CartStore = useCartStore();
		if (!CartStore.summaryData)
			throw new Error("Cart summary data is missing on reservationConfirm");

		if (!CartStore.getItemsDetails().length)
			throw new Error("Cart itemsDetails are missing on reservationConfirm");

		this.setLoadingReservationApi(true);
		const restClient = useReservationRestClient();
		let response: IResponse<IReservationConfirmResponse, IResponseError<ReservationConfirmError>> | null = null;
		const userStore = useUserStore();
		const userData = userStore.userData();
		const { environment, channel } = useSettings();
		setupPaymentsEnv({
			environment: environment.value,
			channel: channel.value,
		}, "mybowlingpassport.com");

		const ReturnUrl = getPaymentsAppUrl();
		const SummaryData = CartStore.summaryData;
		const Items = CartStore.getItemsDetails();
		const Summary = {
			AddedTaxes: SummaryData.AddedTaxes,
			Deposit: SummaryData.Deposit,
			Fee: SummaryData.Fee,
			Total: SummaryData.Total,
			TotalItems: SummaryData.TotalItems,
			AutoGratuity: SummaryData.AutoGratuity,
			TotalWithoutTaxes: SummaryData.TotalWithoutTaxes
		};
		const Cart: IReservationConfirmData = {
			ReturnUrl,
			Items,
			Summary
		};

		if (userStore.isLoggedIn()) {
			response = await restClient.reservationConfirm(
				Number(useSystemStore().info?.Id),
				this.reservationKey,
				Cart
			);
		} else if (userStore.isGuestWithData()) {
			const GuestDetails = {
				Email: userData.Email,
				PhoneNumber: userData.Phone,
				ReferentName: userData.FirstName
			};
			response = await restClient.reservationGuestConfirm(
				Number(useSystemStore().info?.Id),
				this.reservationKey,
				{
					GuestDetails,
					Cart
				}
			);
		} else
			throw new Error("User is not logged in or is a guest without data");

		this.setLoadingReservationApi(false);
		this.setReservationNeedPayment(response?.data?.NeedPayment ?? null);
		return response;
	}

	@Action async getPayPalTransactionIdentifiers(): Promise<TransactionIdentifiers | null> {
		const CartStore = useCartStore();
		if (!this.reservationKey || !CartStore.summaryData) return null;

		this.setLoadingReservationApi(true);
		const restClient = useReservationRestClient();
		let response: IResponse<IReservationConfirmResponse, IResponseError<ReservationConfirmError>> | null = null;
		const userStore = useUserStore();
		const userData = userStore.userData();
		const SummaryData = CartStore.summaryData;
		const { path } = getPaymentPopup();
		const ReturnUrl = new URL(path, location.origin).href;
		const Items = CartStore.getItemsDetails();
		const Summary = {
			AddedTaxes: SummaryData.AddedTaxes,
			Deposit: SummaryData.Deposit,
			Fee: SummaryData.Fee,
			Total: SummaryData.Total,
			TotalItems: SummaryData.TotalItems,
			AutoGratuity: SummaryData.AutoGratuity,
			TotalWithoutTaxes: SummaryData.TotalWithoutTaxes
		};
		const Cart: IReservationConfirmData = {
			ReturnUrl,
			Items,
			Summary
		};

		if (userStore.isLoggedIn()) {
			response = await restClient.reservationConfirm(
				Number(useSystemStore().info?.Id),
				this.reservationKey,
				Cart
			);
		} else if (userStore.isGuestWithData()) {
			const GuestDetails = {
				Email: userData.Email,
				PhoneNumber: userData.Phone,
				ReferentName: userData.FirstName
			};
			response = await restClient.reservationGuestConfirm(
				Number(useSystemStore().info?.Id),
				this.reservationKey,
				{
					GuestDetails,
					Cart
				}
			);
		} else
			throw new Error("User is not logged in or is a guest without data");

		this.setLoadingReservationApi(false);
		if (!response) return null;

		if (response && response.error?.data?.Error?.Code === "ReservationAlreadyConfirmed") {
			this.setReservationStatus("CONFIRMED");
			this.setReservationNeedPayment(false);
			return null;
		} else if ((response && response.status >= 400) || (response && !response.data)) {
			this.setReservationStatus(null);
			return null;
		} else {
			this.setReservationNeedPayment(response?.data?.NeedPayment ?? null);
			if (response.data?.NeedPayment && response.data?.ApprovePayment) {
				this.setReservationStatus(null);

				const isPaypalPayload = (data: any): data is TransactionIdentifiers =>
					data?.OperationId && data?.OrderId;

				if (isPaypalPayload(response.data.ApprovePayment.Data)) {
					this.setOperationId(response.data.ApprovePayment.Data?.OperationId ?? null);
					return response.data.ApprovePayment.Data;
				} else return null;
			}

			return null;
		}
	}

	@Action async syncReservationStatus(systemId: number) {
		if (!this.reservationKey || !this.operationId) return null;
		const restClient = useReservationRestClient();
		const response = await restClient.getReservationStatus(systemId, this.reservationKey, this.operationId);

		if (response?.data) {
			this.setReservationStatus(response.data.ReservationStatus);
			this.setPaymentStatus(response.data.PaymentStatus);
		}
	}

	@Action async ensureOfferOptions(systemId: number) {
		if (!this.reservationKey || !this.offer || this.options) return null;
		const restClient = useReservationRestClient();

		const response = await restClient.getWebOfferOptions(systemId, this.offer.OfferId);
		this.setOptions(response.data);
	}

	@Action async saveBowlers({ systemId, Players, options }: {systemId: number, Players: IReservationBowlerOptions[],  options?: IReservationBowlerRequestOptions}) {
		if (!this.reservationKey) return null;
		const restClient = useReservationRestClient();
		return restClient.saveBowlersOptions(systemId, this.reservationKey, Players, options);
	}

	@Action prepareBowlingMandatoryPage() {
		if (!this.options) return this.setBowlingMandatoryPages(null);

		const checkIfMandatoryPageIsEnabled = (condition: boolean, ...elements) => {
			return condition ? elements : [];
		};

		const mandatoryPagesEnable: IBowlingMandatoryPage[] = [
			...checkIfMandatoryPageIsEnabled(this.options.IsShoesEnabled, {
				name: footwearsPage,
				visited: false
			}),
			...checkIfMandatoryPageIsEnabled(this.options.ShowGamesAndExtraPage, {
				name: extrasPage,
				visited: false
			}),
			...checkIfMandatoryPageIsEnabled(this.options.ShowFoodAndBeveragePage, {
				name: foodBeveragePage,
				visited: false
			})
		];

		return this.setBowlingMandatoryPages(mandatoryPagesEnable);
	}

	@Action ensureBowlingMandatoryPages() {
		if (!this.reservationKey || !this.offer || this.bowlingMandatoryPages) return;
		this.prepareBowlingMandatoryPage();
	}

	private async _syncWithStore(context: IBaseContext) {
		await UserModuleInit;

		const moduleKey = await persistenceKey();
		if (!moduleKey) return this.setPersistenceReady();
		const accessCtx = context.access();
		const [result] = await resolveMutationContext(accessCtx.get<IReservationData>(moduleKey));
		const data: IReservationData = result ?? {
			bowlers: [] as IPlayersQuantity[],
			bowlersTotal: 0,
			shoesTotal: 0,
			latestView: null,
			formPickedDatetime: null,
			pickedDatetime: null,
			reservationStatus: null,
			reservationNeedPayment: null,
			reservationKey: null,
			reservationToken: null,
			reservationKeyExpire: null,
			paymentStatus: null,
			systemId: Number(useSystemStore().info?.Id),
			offer: null
		};
		this.setData(data);
		this.setPersistenceReady();
	}

	@Action initModuleData(context: IBaseContext) {
		return this._syncWithStore(context);
	}

	@Action syncWithStore() {
		// eslint-disable-next-line @typescript-eslint/no-use-before-define
		const ctx = getMutationContext(moduleName);
		if (!ctx) return Promise.resolve();
		return this._syncWithStore(ctx);
	}
}
const moduleName = "reservation";
let ReservationStore: ReservationStoreFactory | null;
function useReservationStore() {
	if (ReservationStore) return ReservationStore;

	const mod = ReservationStore = new ReservationStoreFactory({ store, name: moduleName });

	return mod;
}
export const ReservationModuleInit = usePersistentModule(moduleName, useReservationStore(), {
	async onMutation(context) {
		const ReservationStore = useReservationStore();

		if (!context || !ReservationStore.persistenceInitialized) return;
		if (ReservationStore.isReservationConfirmed() || ReservationStore.isReservationTemporary()) return;

		const moduleKey = await persistenceKey();
		if (!moduleKey) return;

		const accessCtx = context.access();

		if (context.mutationsContains("clearData") || context.mutationsContains("clearConfirmedData"))
			return await resolveMutationContext(accessCtx.delete(moduleKey));

		if (!ReservationStore.hasReservationFormData())
			return;

		if (!context.mutationsContains("ensureRestClient") && !context.mutationsContains("setSummaryData") && !context.mutationsContains("cacheOpeningDay")) {
			const data = deepToRaw(ReservationStore.getData());
			await resolveMutationContext(accessCtx.put(data, moduleKey));
		}
	}
});

export default useReservationStore;
